/**
 * Relation schema validation with Zod
 * @module schemas/relation
 */

import { z } from 'zod';
import {
  RELATION_TYPES,
  RELATION_STRENGTHS,
  USAGE_TYPES,
  DEPENDENCY_TYPES,
  OWNERSHIP_TYPES,
  INTEGRATION_METHODS,
  DATA_FLOWS,
  RELATION_DIRECTIONS,
} from '../constants/relation-types.js';
import { userSummarySchema } from './user.schema.js';
import { objectSummarySchema, objectTypeSchema } from './object.schema.js';
import { uuidSchema } from './common.schema.js';

// =============================================================================
// Enum Schemas
// =============================================================================

export const relationTypeSchema = z.enum(RELATION_TYPES);
export const relationStrengthSchema = z.enum(RELATION_STRENGTHS);
export const usageTypeSchema = z.enum(USAGE_TYPES);
export const dependencyTypeSchema = z.enum(DEPENDENCY_TYPES);
export const ownershipTypeSchema = z.enum(OWNERSHIP_TYPES);
export const integrationMethodSchema = z.enum(INTEGRATION_METHODS);
export const dataFlowSchema = z.enum(DATA_FLOWS);
export const relationDirectionSchema = z.enum(RELATION_DIRECTIONS);

// =============================================================================
// Relation Attribute Schemas
// =============================================================================

/** REALIZES attributes */
export const realizesAttributesSchema = z.object({
  strength: relationStrengthSchema.optional(),
});

/** USES attributes */
export const usesAttributesSchema = z.object({
  usageType: usageTypeSchema.optional(),
});

/** DEPENDS_ON attributes */
export const dependsOnAttributesSchema = z.object({
  dependencyType: dependencyTypeSchema.optional(),
});

/** OWNS attributes */
export const ownsAttributesSchema = z.object({
  ownershipType: ownershipTypeSchema.optional(),
});

/** BELONGS_TO attributes */
export const belongsToAttributesSchema = z.object({
  role: z.string().max(100).optional(),
});

/** INTEGRATES_WITH attributes */
export const integratesWithAttributesSchema = z.object({
  integrationMethod: integrationMethodSchema.optional(),
  dataFlow: dataFlowSchema.optional(),
});

/** Union of all relation attributes */
export const relationAttributesSchema = z.union([
  realizesAttributesSchema,
  usesAttributesSchema,
  dependsOnAttributesSchema,
  ownsAttributesSchema,
  belongsToAttributesSchema,
  integratesWithAttributesSchema,
]);

// =============================================================================
// Request Schemas
// =============================================================================

/** Create relation request schema */
export const createRelationRequestSchema = z.object({
  sourceId: uuidSchema,
  targetId: uuidSchema,
  type: relationTypeSchema,
  attributes: relationAttributesSchema.optional(),
});

// =============================================================================
// Response Schemas
// =============================================================================

/** Relation response schema */
export const relationResponseSchema = z.object({
  id: uuidSchema,
  type: relationTypeSchema,
  sourceId: uuidSchema,
  targetId: uuidSchema,
  source: objectSummarySchema,
  target: objectSummarySchema,
  attributes: relationAttributesSchema,
  createdBy: userSummarySchema,
  createdAt: z.string().datetime(),
});

/** Relation list response schema */
export const relationListResponseSchema = z.object({
  incoming: z.array(relationResponseSchema),
  outgoing: z.array(relationResponseSchema),
});

// =============================================================================
// Graph Traversal Schemas
// =============================================================================

/** Graph node schema */
export const graphNodeSchema = z.object({
  id: uuidSchema,
  type: objectTypeSchema,
  name: z.string(),
  status: z.enum(['draft', 'active', 'deprecated', 'retired']),
  spaceId: uuidSchema,
  distance: z.number().int().nonnegative(),
});

/** Graph edge schema */
export const graphEdgeSchema = z.object({
  id: uuidSchema,
  sourceId: uuidSchema,
  targetId: uuidSchema,
  type: relationTypeSchema,
});

/** Graph traversal response schema */
export const graphTraversalResponseSchema = z.object({
  startObject: objectSummarySchema,
  nodes: z.array(graphNodeSchema),
  edges: z.array(graphEdgeSchema),
  metadata: z.object({
    depth: z.number().int().positive(),
    nodeCount: z.number().int().nonnegative(),
    edgeCount: z.number().int().nonnegative(),
  }),
});

// =============================================================================
// Query Schemas
// =============================================================================

/** List relations query params */
export const listRelationsQuerySchema = z.object({
  direction: relationDirectionSchema.default('both'),
  types: z.array(relationTypeSchema).optional(),
});

/** Graph traversal query params */
export const graphTraversalQuerySchema = z.object({
  startId: uuidSchema,
  depth: z.coerce.number().int().min(1).max(10).default(3),
  types: z.array(relationTypeSchema).optional(),
  direction: relationDirectionSchema.default('outgoing'),
  objectTypes: z.array(objectTypeSchema).optional(),
});

// =============================================================================
// Type Inference
// =============================================================================

export type RelationTypeInput = z.infer<typeof relationTypeSchema>;
export type RelationStrengthInput = z.infer<typeof relationStrengthSchema>;
export type UsageTypeInput = z.infer<typeof usageTypeSchema>;
export type DependencyTypeInput = z.infer<typeof dependencyTypeSchema>;
export type OwnershipTypeInput = z.infer<typeof ownershipTypeSchema>;
export type IntegrationMethodInput = z.infer<typeof integrationMethodSchema>;
export type DataFlowInput = z.infer<typeof dataFlowSchema>;
export type RelationDirectionInput = z.infer<typeof relationDirectionSchema>;
export type RelationAttributesInput = z.infer<typeof relationAttributesSchema>;
export type CreateRelationRequestInput = z.infer<typeof createRelationRequestSchema>;
export type RelationResponseInput = z.infer<typeof relationResponseSchema>;
export type RelationListResponseInput = z.infer<typeof relationListResponseSchema>;
export type GraphNodeInput = z.infer<typeof graphNodeSchema>;
export type GraphEdgeInput = z.infer<typeof graphEdgeSchema>;
export type GraphTraversalResponseInput = z.infer<typeof graphTraversalResponseSchema>;
export type ListRelationsQueryInput = z.infer<typeof listRelationsQuerySchema>;
export type GraphTraversalQueryInput = z.infer<typeof graphTraversalQuerySchema>;
